import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Drawer, IconButton, InputAdornment, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DatePicker2 from "../../components/DatePicker2";
import TerritorySelect from "../../components/AsyncSelects/TerritorySelect";
import { getSalesAnalytics } from "../../distflowAPI/salesAnalyticsApi";
import useAppSelector from "../../hooks/useAppSelector";
import SalesExplorer from "./SalesExplorer";
import { previousPeriodComparison, resolveDateRange } from "./dateRanges";
import "./SalesAnalytics.css";

dayjs.extend(utc);

const DIMENSIONS = [
  { key: "route", title: "By route", icon: <RouteOutlinedIcon fontSize="small" />, description: "Where deliveries are selling" },
  { key: "customer", title: "By customer", icon: <PeopleOutlineIcon fontSize="small" />, description: "Your highest value customers" },
  { key: "vendor", title: "By vendor", icon: <StorefrontOutlinedIcon fontSize="small" />, description: "Sales across your suppliers" },
  { key: "product_category", title: "By product category", icon: <CategoryOutlinedIcon fontSize="small" />, description: "Which products lead growth" },
  { key: "account_type", title: "By account type", icon: <ApartmentOutlinedIcon fontSize="small" />, description: "Performance by account group" },
  { key: "salesman", title: "By salesmen / office", icon: <BadgeOutlinedIcon fontSize="small" />, description: "Sales by team attribution" },
];
const DATE_RANGES = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This week to date" },
  { value: "last_week", label: "Last full week" },
  { value: "mtd", label: "Month to date" },
  { value: "last_month", label: "Last full month" },
  { value: "ytd", label: "Year to date" },
  { value: "custom", label: "Custom dates" },
];
const COMPARISONS = [
  { value: "previous_period", label: "Previous period" },
  { value: "previous_month", label: "Same period last month" },
  { value: "previous_hebrew_year", label: "Same period last Hebrew year" },
  { value: "custom", label: "Custom dates" },
  { value: "none", label: "No comparison" },
];
const ALL_TERRITORIES = { value: null, label: "All" };
const SORT_OPTIONS = [
  { value: "sales", label: "Current sales" },
  { value: "change_amount", label: "Change $" },
  { value: "change_percent", label: "Change %" },
  { value: "share_percent", label: "Share" },
  { value: "name", label: "Name" },
];
const moneyFormat = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const countFormat = new Intl.NumberFormat("en-US");
const money = (value) => moneyFormat.format(Number(value || 0));
const signedMoney = (value) => `${Number(value || 0) > 0 ? "+" : Number(value || 0) < 0 ? "−" : ""}${money(Math.abs(Number(value || 0)))}`;
const percent = (value) => value == null ? "No baseline" : `${Number(value) > 0 ? "+" : ""}${Number(value).toFixed(2)}%`;
const dateLabel = (date) => date ? dayjs.utc(date).format("MMM D, YYYY") : "—";
const rangeLabel = (range) => range ? `${dateLabel(range.start_date)} – ${dateLabel(range.end_date)}` : "";
const comparisonLabel = (mode) => COMPARISONS.find((item) => item.value === mode)?.label || "Comparison";
const direction = (value) => Number(value || 0) > 0 ? "gain" : Number(value || 0) < 0 ? "decline" : "flat";
const share = (value) => value == null ? "—" : `${Number(value).toFixed(2)}%`;

function PercentChange({ value, compact = false }) {
  if (value === undefined) return null;
  const number = value === null ? null : Number(value);
  const movement = number === null ? "new" : number > 0 ? "up" : number < 0 ? "down" : "flat";
  return <span className={`sales-analytics-change sales-analytics-change--${movement}`}>
    {number === null ? "No baseline" : `${number > 0 ? "+" : ""}${number.toFixed(2)}%`}
    {!compact && <span className="sales-analytics-change-caption"> vs. comparison</span>}
  </span>;
}

function MetricCard({ label, value, change, featured = false }) {
  return <div className={`sales-analytics-metric ${featured ? "sales-analytics-metric--featured" : ""}`}>
    <div className="sales-analytics-metric-label">{label}</div>
    <div className="sales-analytics-metric-value">{value}</div>
    <div className="sales-analytics-metric-bottom">{change !== undefined ? <PercentChange value={change} /> : "Selected period"}</div>
  </div>;
}

function BreakdownCard({ dimension, rows, onOpen, loading }) {
  const preview = rows.slice(0, 5);
  return <section className="sales-analytics-breakdown-card" role="button" tabIndex={loading ? -1 : 0}
    aria-disabled={loading} aria-label={`View ${dimension.key.replaceAll("_", " ")} breakdown`}
    onClick={loading ? undefined : onOpen}
    onKeyDown={(event) => {
      if (!loading && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); onOpen(); }
    }}>
    <div className="sales-analytics-breakdown-heading">
      <span className="sales-analytics-dimension-icon" aria-hidden="true">{dimension.icon}</span>
      <div><h3>{dimension.title}</h3><p>{dimension.description}</p></div>
      <span className="sales-analytics-breakdown-count">{rows.length}</span>
    </div>
    <div className="sales-analytics-preview">
      {loading ? <div className="sales-analytics-preview-empty">Loading breakdown…</div> : preview.length ?
        preview.map((row, index) => <div className="sales-analytics-preview-row" key={row.key ?? `${row.id ?? row.name}-${index}`}>
          <span className="sales-analytics-preview-rank">{index + 1}</span>
          <span className="sales-analytics-preview-name" title={row.name}>{row.name || "Unassigned"}</span>
          <strong>{money(row.sales)}</strong>
        </div>) : <div className="sales-analytics-preview-empty">No sales in this period</div>}
    </div>
    <span className="sales-analytics-view-breakdown">View breakdown <ArrowForwardIcon fontSize="small" /></span>
  </section>;
}

function InsightCard({ eyebrow, title, value, detail, tone = "neutral" }) {
  return <div className={`sales-analytics-insight sales-analytics-insight--${tone}`}>
    <span className="sales-analytics-insight-eyebrow">{eyebrow}</span>
    <strong>{title || "No data yet"}</strong>
    {value && <span className="sales-analytics-insight-value">{value}</span>}
    {detail && <span className="sales-analytics-insight-detail">{detail}</span>}
  </div>;
}

function Delta({ amount, percentage }) {
  const movement = direction(amount);
  return <span className={`sales-analytics-delta sales-analytics-delta--${movement}`}>
    <span aria-hidden="true">{movement === "gain" ? "↑" : movement === "decline" ? "↓" : "→"}</span>{" "}
    {signedMoney(amount)} <span className="sales-analytics-delta-percent">{percent(percentage)}</span>
  </span>;
}

function BreakdownDrawer({ dimension, rows, report, onClose }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("sales");
  const [movement, setMovement] = useState("all");
  const hasComparison = Boolean(report?.comparison);
  const filteredRows = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    return rows.filter((row) =>
      (!term || String(row.name || "Unassigned").toLocaleLowerCase().includes(term)) &&
      (movement === "all" || (movement === "gaining" && Number(row.change_amount) > 0) ||
        (movement === "declining" && Number(row.change_amount) < 0))
    ).sort((a, b) => {
      if (sort === "name") return String(a.name || "").localeCompare(String(b.name || ""));
      const aValue = a[sort] == null ? -Infinity : Number(a[sort]);
      const bValue = b[sort] == null ? -Infinity : Number(b[sort]);
      return bValue - aValue || String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [rows, query, sort, movement]);
  return <Drawer anchor="right" open={Boolean(dimension)} onClose={onClose} sx={{ zIndex: 1600 }}
    PaperProps={{ className: "sales-analytics-drawer", role: "dialog", "aria-labelledby": "sales-analytics-drawer-title" }}>
    {dimension && <>
      <header className="sales-analytics-drawer-header">
        <div><span className="sales-analytics-eyebrow">Full breakdown</span>
          <h2 id="sales-analytics-drawer-title">{dimension.title}</h2>
          <p>{rangeLabel(report?.date_range)}</p>
          {hasComparison && <p>Compared with {rangeLabel(report.comparison)}</p>}
        </div>
        <IconButton aria-label="Close breakdown" onClick={onClose}><CloseIcon /></IconButton>
      </header>
      <div className="sales-analytics-drawer-summary"><span>Total sales</span><strong>{money(report?.summary?.total_sales)}</strong><span>{rows.length} {rows.length === 1 ? "entry" : "entries"}</span></div>
      <div className="sales-analytics-drawer-controls">
        <TextField size="small" placeholder={`Search ${dimension.title.slice(3).toLowerCase()}`} value={query}
          onChange={(event) => setQuery(event.target.value)} inputProps={{ "aria-label": `Search ${dimension.title.slice(3).toLowerCase()}` }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Sort breakdown" value={sort} onChange={(event) => setSort(event.target.value)}>
          {SORT_OPTIONS.filter((option) => hasComparison || !["change_amount", "change_percent"].includes(option.value)).map((option) =>
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
        </TextField>
        {hasComparison && <TextField select size="small" label="Filter movement" value={movement} onChange={(event) => setMovement(event.target.value)}>
          <MenuItem value="all">All</MenuItem><MenuItem value="gaining">Gaining</MenuItem><MenuItem value="declining">Declining</MenuItem>
        </TextField>}
      </div>
      <TableContainer className="sales-analytics-drawer-table-wrap"><Table size="small" stickyHeader aria-label={`${dimension.title} sales table`}>
        <TableHead><TableRow>
          <TableCell>{dimension.title.slice(3)}</TableCell><TableCell align="right">Current sales</TableCell>
          {hasComparison && <TableCell align="right">Previous sales</TableCell>}
          {hasComparison && <TableCell align="right">Change</TableCell>}
          <TableCell align="right">Share</TableCell>
        </TableRow></TableHead>
        <TableBody>{filteredRows.map((row, index) => <TableRow key={row.key ?? `${row.id ?? row.name}-${index}`}>
          <TableCell className="sales-analytics-drawer-name">{row.name || "Unassigned"}</TableCell>
          <TableCell align="right" data-label="Current sales">{money(row.sales)}</TableCell>
          {hasComparison && <TableCell align="right" data-label="Previous sales">{money(row.previous_sales)}</TableCell>}
          {hasComparison && <TableCell align="right" data-label="Change"><Delta amount={row.change_amount} percentage={row.change_percent} /></TableCell>}
          <TableCell align="right" data-label="Share">{row.share_percent == null ? "—" : `${Number(row.share_percent).toFixed(2)}%`}</TableCell>
        </TableRow>)}
          {!filteredRows.length && <TableRow><TableCell colSpan={hasComparison ? 5 : 3} align="center" className="sales-analytics-table-empty">
            {rows.length ? "No matching entries. Try another search or filter." : "No entries for this breakdown."}
          </TableCell></TableRow>}
        </TableBody>
      </Table></TableContainer>
      <footer className="sales-analytics-drawer-footer"><span>Showing {filteredRows.length} of {rows.length} entries</span><strong>{money(report?.summary?.total_sales)}</strong></footer>
    </>}
  </Drawer>;
}

function SalesAnalytics() {
  const isStaff = useAppSelector((state) => state.user.authUser?.is_staff === true);
  const [initialRange] = useState(() => resolveDateRange("mtd"));
  const [rangePreset, setRangePreset] = useState("mtd");
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [territory, setTerritory] = useState(ALL_TERRITORIES);
  const [comparison, setComparison] = useState("previous_period");
  const [appliedComparison, setAppliedComparison] = useState("previous_period");
  const [comparisonStartDate, setComparisonStartDate] = useState(null);
  const [comparisonEndDate, setComparisonEndDate] = useState(null);
  const [applied, setApplied] = useState(() => ({
    start_date: initialRange.startDate.format("YYYY-MM-DD"),
    end_date: initialRange.endDate.format("YYYY-MM-DD"),
    ...previousPeriodComparison(initialRange.startDate, initialRange.endDate),
  }));
  const [appliedDraft, setAppliedDraft] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    if (!isStaff) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setReport(null);
    setRequestError("");
    getSalesAnalytics(applied, { signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setReport(data); })
      .catch((error) => {
        if (controller.signal.aborted || error?.code === "ERR_CANCELED") return;
        setRequestError(error?.response?.status === 403
          ? "Only admins can view sales analytics."
          : "Sales analytics could not be loaded. Please try again.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [applied, isStaff]);

  function selectRangePreset(value) {
    setRangePreset(value);
    if (value !== "custom") {
      const range = resolveDateRange(value);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
    }
  }
  const draftSignature = JSON.stringify({ rangePreset,
    start: startDate?.isValid() ? startDate.format("YYYY-MM-DD") : "",
    end: endDate?.isValid() ? endDate.format("YYYY-MM-DD") : "",
    territory: territory?.value ?? null, comparison,
    comparisonStart: comparisonStartDate?.isValid() ? comparisonStartDate.format("YYYY-MM-DD") : "",
    comparisonEnd: comparisonEndDate?.isValid() ? comparisonEndDate.format("YYYY-MM-DD") : "",
  });
  const initialSignature = JSON.stringify({ rangePreset: "mtd", start: initialRange.startDate.format("YYYY-MM-DD"),
    end: initialRange.endDate.format("YYYY-MM-DD"), territory: null, comparison: "previous_period", comparisonStart: "", comparisonEnd: "" });
  const dirty = draftSignature !== (appliedDraft || initialSignature);

  function applyFilters(event) {
    event.preventDefault();
    const range = rangePreset === "custom" ? { startDate, endDate } : resolveDateRange(rangePreset);
    const selectedStart = range.startDate;
    const selectedEnd = range.endDate;
    if (!selectedStart?.isValid() || !selectedEnd?.isValid()) {
      setValidationError("Choose valid start and end dates."); return;
    }
    if (selectedEnd.isBefore(selectedStart, "day")) {
      setValidationError("End date must be on or after start date."); return;
    }
    if (comparison === "custom" && (!comparisonStartDate?.isValid() || !comparisonEndDate?.isValid())) {
      setValidationError("Choose both custom comparison dates."); return;
    }
    if (comparison === "custom" && comparisonEndDate.isBefore(comparisonStartDate, "day")) {
      setValidationError("Comparison end date must be on or after its start date."); return;
    }
    setValidationError("");
    setSelectedDimension(null);
    setStartDate(selectedStart);
    setEndDate(selectedEnd);
    setAppliedComparison(comparison);
    setAppliedDraft(draftSignature);
    setFiltersOpen(false);
    setApplied({
      start_date: selectedStart.format("YYYY-MM-DD"), end_date: selectedEnd.format("YYYY-MM-DD"),
      territory_id: territory?.value,
      ...(comparison === "previous_period" ? previousPeriodComparison(selectedStart, selectedEnd) : { comparison }),
      ...(comparison === "custom" ? {
        comparison_start_date: comparisonStartDate.format("YYYY-MM-DD"),
        comparison_end_date: comparisonEndDate.format("YYYY-MM-DD"),
      } : {}),
    });
  }

  if (!isStaff) return <div className="sales-analytics-page"><div className="sales-analytics-access">
    <h1>Sales analytics</h1><p>Only admins can view sales analytics.</p></div></div>;

  const summary = report?.summary;
  const breakdowns = report?.breakdowns || {};
  const insights = report?.insights || {};
  const hasComparison = Boolean(report?.comparison);
  const rows = selectedDimension ? breakdowns[selectedDimension.key] || [] : [];
  const decline = insights.largest_decline;
  const decliningCustomers = insights.declining_customers || [];
  const draftRange = startDate?.isValid() && endDate?.isValid()
    ? { start_date: startDate.format("YYYY-MM-DD"), end_date: endDate.format("YYYY-MM-DD") } : null;
  const empty = report && Number(summary?.total_sales || 0) === 0 &&
    Number(summary?.previous_total_sales || 0) === 0 &&
    !Object.values(report.breakdowns || {}).some((list) => list?.length);
  return <div className="sales-analytics-page">
    <div className="sales-analytics-topline">
      <div className="sales-analytics-page-heading"><span className="sales-analytics-eyebrow">Accounting / Reports</span>
        <h1>Sales analytics</h1><p>Understand what’s driving your sales, and how the mix is changing.</p></div>
      <div className="sales-analytics-filter-area">
        <div className="sales-analytics-mobile-filter-summary">
          <span>Applied: {report ? rangeLabel(report.date_range) : rangeLabel(applied)}{hasComparison ? ` · compared with ${rangeLabel(report.comparison)}` : ""}</span>
          <Button startIcon={<FilterListIcon />} aria-expanded={filtersOpen} aria-controls="sales-analytics-filters" onClick={() => setFiltersOpen(!filtersOpen)}>Filters</Button>
        </div>
        <form id="sales-analytics-filters" className={`sales-analytics-filters ${filtersOpen ? "sales-analytics-filters--open" : ""}`} onSubmit={applyFilters}>
          <div className="sales-analytics-filter-field"><TextField select fullWidth size="small" label="Date range" value={rangePreset} onChange={(event) => selectRangePreset(event.target.value)}>
            {DATE_RANGES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </TextField></div>
          <div className="sales-analytics-range-preview" aria-live="polite"><span>Selected dates · New York time</span><strong>{draftRange ? rangeLabel(draftRange) : "Choose dates"}</strong></div>
          <div className="sales-analytics-filter-field"><TerritorySelect label="Territory" value={territory} extraOptions={[ALL_TERRITORIES]} onChange={(_event, value) => setTerritory(value)}
            isOptionEqualToValue={(option, value) => option.value === value.value} placeholder="All territories" /></div>
          <div className="sales-analytics-filter-field"><TextField select fullWidth size="small" label="Compare to" value={comparison} onChange={(event) => setComparison(event.target.value)}>
            {COMPARISONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </TextField>{hasComparison && !dirty && <small className="sales-analytics-applied-comparison">{rangeLabel(report.comparison)}</small>}</div>
          <Button className="sales-analytics-apply" type="submit" variant="contained" disabled={loading}>Apply</Button>
          {rangePreset === "custom" && <div className="sales-analytics-custom-dates">
            <DatePicker2 label="Start date" controlledValue={startDate} setControlledValue={setStartDate} size="small" />
            <DatePicker2 label="End date" controlledValue={endDate} setControlledValue={setEndDate} size="small" />
          </div>}
          {comparison === "custom" && <div className="sales-analytics-custom-dates">
            <DatePicker2 label="Comparison start" controlledValue={comparisonStartDate} setControlledValue={setComparisonStartDate} size="small" />
            <DatePicker2 label="Comparison end" controlledValue={comparisonEndDate} setControlledValue={setComparisonEndDate} size="small" />
          </div>}
          {dirty && <span className="sales-analytics-dirty" role="status">Unapplied changes</span>}
          {validationError && <Alert severity="error" className="sales-analytics-filter-alert">{validationError}</Alert>}
        </form>
      </div>
    </div>
    {requestError && <Alert severity="error" className="sales-analytics-request-alert" action={<Button color="inherit" size="small" onClick={() => setApplied({ ...applied })}>Retry</Button>}>{requestError}</Alert>}
    {loading && !report && <div className="sales-analytics-loading" role="status"><CircularProgress size={28} /><span>Loading sales analytics…</span></div>}
    {empty && <section className="sales-analytics-empty"><h2>No sales for this selection</h2><p>Choose another date range or territory to explore sales activity.</p></section>}
    {report && !empty && <div className="sales-analytics-content">
      <section className="sales-analytics-summary" aria-label="Sales summary">
        <div className="sales-analytics-section-head sales-analytics-summary-head">
          <div><span className="sales-analytics-eyebrow">The overview</span><h2>Sales at a glance</h2></div>
          <div className="sales-analytics-summary-actions">
            {hasComparison && <span className="sales-analytics-comparison-note">Compared with {comparisonLabel(appliedComparison)} · {rangeLabel(report.comparison)}</span>}
            <Button className="sales-analytics-accounting-info" startIcon={<InfoOutlinedIcon fontSize="small" />} onClick={() => setInfoOpen(true)}>How sales are calculated</Button>
          </div>
        </div>
        <div className="sales-analytics-metrics">
          <MetricCard featured label="Total sales" value={money(summary?.total_sales)} change={hasComparison ? summary?.sales_change_percent : undefined} />
          <MetricCard label="Orders" value={countFormat.format(summary?.total_orders || 0)} change={hasComparison ? summary?.total_orders_change_percent : undefined} />
          <MetricCard label="Customers" value={countFormat.format(summary?.total_customers || 0)} change={hasComparison ? summary?.total_customers_change_percent : undefined} />
          <MetricCard label="Active accounts" value={countFormat.format(summary?.active_accounts || 0)} change={hasComparison ? summary?.active_accounts_change_percent : undefined} />
          <MetricCard label="Average order size" value={money(summary?.average_order_size)} change={hasComparison ? summary?.average_order_size_change_percent : undefined} />
        </div>
      </section>
      <SalesExplorer report={report} onOpenBreakdown={(dimensionKey) => setSelectedDimension(DIMENSIONS.find((item) => item.key === dimensionKey) || null)} />
      <section className="sales-analytics-breakdowns" aria-label="Sales breakdowns">
        <div className="sales-analytics-section-head">
          <div><span className="sales-analytics-eyebrow">Find the pattern</span><h2>Break down your sales</h2></div>
          <span className="sales-analytics-section-aside">Select a category to see every entry</span>
        </div>
        <div className="sales-analytics-breakdown-grid">
          {DIMENSIONS.map((dimension) => <BreakdownCard key={dimension.key} dimension={dimension}
            rows={breakdowns[dimension.key] || []} onOpen={() => setSelectedDimension(dimension)} loading={loading} />)}
        </div>
      </section>
      <section className="sales-analytics-insights" aria-label="Sales insights">
        <div className="sales-analytics-section-head"><div><span className="sales-analytics-eyebrow">What stands out</span><h2>Insights</h2></div></div>
        <div className="sales-analytics-insights-grid">
          <InsightCard eyebrow="Top route" title={insights.top_route?.name} value={insights.top_route && money(insights.top_route.sales)}
            detail={insights.top_route && `${share(insights.top_route.share_percent)} of total sales`} />
          <InsightCard eyebrow="Top customer" title={insights.top_customer?.name} value={insights.top_customer && money(insights.top_customer.sales)}
            detail={insights.top_customer && `${share(insights.top_customer.share_percent)} of total sales`} />
          <InsightCard eyebrow="Fastest growing category" title={insights.fastest_growing_category?.name}
            value={insights.fastest_growing_category && <PercentChange value={insights.fastest_growing_category.change_percent} compact />}
            detail={insights.fastest_growing_category ? "vs. comparison period" : hasComparison ? "No growth recorded" : "Choose a comparison to see growth"} tone="growth" />
          <InsightCard eyebrow="Largest decline" title={decline?.name} value={decline && money(decline.change_amount)}
            detail={decline?.dimension ? `In ${String(decline.dimension).replaceAll("_", " ")}` : decline ? "Across all breakdowns" : hasComparison ? "No decline recorded" : "Choose a comparison to see declines"} tone="decline" />
          <InsightCard eyebrow="Declining customers" title={hasComparison ? `${decliningCustomers.length} ${decliningCustomers.length === 1 ? "customer" : "customers"}` : "No comparison"}
            detail={hasComparison ? decliningCustomers.length ? "See the list below" : "No customers declined" : "Choose a comparison to see changes"} />
        </div>
        {hasComparison && decliningCustomers.length > 0 && <div className="sales-analytics-declining-list">
          <div className="sales-analytics-declining-title">Customers with falling sales</div>
          <div className="sales-analytics-declining-grid">
            {decliningCustomers.map((customer, index) => <div className="sales-analytics-declining-row" key={customer.key ?? `${customer.id ?? customer.name}-${index}`}>
              <span>{customer.name || "Unassigned"}</span><strong>{money(customer.change_amount)}</strong>
            </div>)}
          </div>
        </div>}
      </section>
    </div>}
    <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} aria-labelledby="sales-analytics-info-title">
      <DialogTitle id="sales-analytics-info-title">How sales are calculated</DialogTitle>
      <DialogContent><p>Sales use order totals on fulfillment dates. Canceled orders and voided invoices are excluded; payment dates do not change sales. Returns and signed corrections can reduce a breakdown row.</p>
        <p>Route, vendor, and product category use delivered items. Unallocated adjustments include fees, unknown routes, and differences from the stored order total. Every breakdown reconciles to total sales.</p>
        <p>Reporting dates use New York time. A comparison shows the dates actually applied by the report.</p></DialogContent>
      <DialogActions><Button onClick={() => setInfoOpen(false)}>Close</Button></DialogActions>
    </Dialog>
    <BreakdownDrawer key={selectedDimension?.key || "closed"} dimension={selectedDimension} rows={rows} report={report} onClose={() => setSelectedDimension(null)} />
  </div>;
}
export default SalesAnalytics;
